import { useState } from 'react';
import { saveLocation, deleteLocation, getGeoPos } from '../lib/supabase';

const EMPTY = { id: null, name: '', lat: '', lng: '', radius: 100 };

// Sedes de la organización: lista, alta, edición y baja. El límite lo define el plan.
export default function SedesAdmin({ locations, org, profile, onChanged, showToast }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const max = org?.max_locations;
  const full = max != null && locations.length >= max;

  const detect = () =>
    getGeoPos()
      .then((p) => setForm((f) => ({ ...f, lat: +p.coords.latitude.toFixed(6), lng: +p.coords.longitude.toFixed(6) })))
      .catch(() => showToast('GPS no disponible', 'error'));

  const save = async () => {
    const lat = parseFloat(form.lat), lng = parseFloat(form.lng), radius = parseInt(form.radius, 10);
    if (!form.name.trim() || isNaN(lat) || isNaN(lng) || !radius) { showToast('Completá nombre, ubicación y radio', 'error'); return; }
    setBusy(true);
    try {
      await saveLocation({ id: form.id, name: form.name.trim(), lat, lng, radius, updatedBy: profile.id });
      showToast(form.id ? 'Sede actualizada' : 'Sede creada');
      setForm(null);
      await onChanged();
    } catch (e) { showToast(e.message || 'No se pudo guardar', 'error'); }
    setBusy(false);
  };

  const remove = async (l) => {
    if (!window.confirm(`¿Eliminar la sede "${l.name}"? Los empleados asignados quedarán sin sede fija.`)) return;
    try { await deleteLocation(l.id); showToast('Sede eliminada'); await onChanged(); }
    catch (e) { showToast(e.message || 'No se pudo eliminar', 'error'); }
  };

  const field = 'w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50';

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-bold text-gray-800">Sedes</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {locations.length}{max != null ? ` de ${max}` : ''} · el personal solo puede marcar dentro del radio de su sede
          </p>
        </div>
        {!form && (
          <button disabled={full} onClick={() => setForm({ ...EMPTY })}
            className="px-3 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-40 flex-shrink-0"
            style={{ background: 'var(--brand-grad)' }}>+ Nueva sede</button>
        )}
      </div>
      {full && !form && <p className="text-xs text-amber-600">Llegaste al límite de sedes de tu plan. Pasate a un plan superior para agregar más.</p>}
      {locations.length === 0 && !form && (
        <p className="text-xs text-gray-500 bg-gray-50 rounded-2xl p-3">Todavía no cargaste ninguna sede. Mientras tanto, el personal puede marcar desde cualquier lugar.</p>
      )}

      {locations.map((l) => (
        <div key={l.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-2xl px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">📍 {l.name}</p>
            <p className="text-xs text-gray-400">Radio {l.radius_meters} m · {Number(l.lat).toFixed(4)}, {Number(l.lng).toFixed(4)}</p>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <button onClick={() => setForm({ id: l.id, name: l.name, lat: l.lat, lng: l.lng, radius: l.radius_meters })} className="text-xs font-bold text-sky-600 px-2 py-1">Editar</button>
            <button onClick={() => remove(l)} className="text-xs font-bold text-red-500 px-2 py-1">Quitar</button>
          </div>
        </div>
      ))}

      {form && (
        <div className="space-y-3 border-t border-gray-100 pt-4">
          <input placeholder="Nombre de la sede" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" placeholder="Latitud" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className={field} />
            <input type="number" placeholder="Longitud" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className={field} />
          </div>
          <input type="number" placeholder="Radio (metros)" value={form.radius} onChange={(e) => setForm({ ...form, radius: e.target.value })} className={field} />
          <button onClick={detect} className="w-full py-2.5 rounded-2xl border-2 border-dashed border-sky-200 text-sm font-semibold text-sky-600 hover:bg-sky-50">📍 Detectar mi ubicación actual</button>
          <div className="flex gap-3">
            <button onClick={() => setForm(null)} className="flex-1 py-3 rounded-2xl text-sm font-bold border-2 border-gray-200 text-gray-500">Cancelar</button>
            <button disabled={busy} onClick={save} className="flex-1 py-3 rounded-2xl text-sm font-bold text-white disabled:opacity-50" style={{ background: 'var(--brand-grad)' }}>{busy ? 'Guardando…' : 'Guardar sede'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
